import { AutocompleteWithLabel } from '@adotel/components'
import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { Button, Grid, Grow, Switch, Typography } from '@material-ui/core'
import { useState, useEffect, FC, useRef } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { DateObject } from '../../helpers/date'
import { getTranslate } from '../../localization'
import { ProformaStatus } from '../../types/walletEnums'
import { ProformaBrief, ProformaFilter } from '../../types/walletProforma'
import useStyles from './styles/FilterBox.styles'

type FilterBoxDesktopProps = {
  filters: ProformaFilter
  setfilters: (v: ProformaFilter) => void
  setPage: (v: number) => void
  getProformas: LazyQueryExecFunction<
    {
      proformas: ProformaBrief[]
    },
    OperationVariables
  >
  loadingForProformas: boolean
}

export const FilterBoxDesktop: FC<FilterBoxDesktopProps> = ({
  filters,
  setfilters,
  getProformas,
  loadingForProformas,
  setPage
}) => {
  const classes = useStyles()

  const [timeRangeDateObject, settimeRangeDateObject] = useState<DateObject>({
    gregorianStartDate: filters.timeRange?.from || '',
    gregorianEndDate: filters.timeRange?.to || ''
  })
  const [timeRangeDatePickerIsOpen, settimeRangeDatePickerIsOpen] =
    useState(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const datePickerTransactionTimeRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerTransactionTimeRef.current &&
        !datePickerTransactionTimeRef?.current?.contains(
          event.target as Node
        ) &&
        timeRangeDatePickerIsOpen
      ) {
        settimeRangeDatePickerIsOpen(false)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerTransactionTimeRef, timeRangeDatePickerIsOpen])
  const proformaStatuses: ProformaStatus[] = [
    'paid',
    'pendingPayment',
    'cancelled',
    'confirmed',
    'expired',
    'paid',
    'pendingConfirmation',
    'pendingPayment',
    'pendingPaymentConfirmation',
    'rejected'
  ]
  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.filterContainer}>
          <AutocompleteWithLabel
            options={proformaStatuses.map((status) => ({
              label: status,
              value: status,
              id: status
            }))}
            inputSize="small"
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) =>
              option.label.toLowerCase().includes(value.label.toLowerCase()) ===
              true
            }
            onChange={(_, value: any) => {
              if (value) {
                setfilters({
                  ...filters,
                  status: value.value
                })
              } else {
                setfilters({
                  ...filters,
                  status: undefined
                })
              }
            }}
            label={getTranslate('Status')}
          />
          <div ref={datePickerTransactionTimeRef}>
            <Typography>{getTranslate('Time Range')}</Typography>
            <TimePeriodForm
              left
              isOpen={timeRangeDatePickerIsOpen}
              setTimePickerSelected={settimeRangeDatePickerIsOpen}
              dateObject={timeRangeDateObject}
              onChange={(data: DateObject) => {
                settimeRangeDateObject(data)
                setfilters({
                  ...filters,
                  timeRange: {
                    from: data.gregorianStartDate || '',
                    to: data.gregorianEndDate || ''
                  }
                })
              }}
              onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
              setOnDoneClickForCloseDatePicker={
                setOnDoneClickForCloseDatePicker
              }
              enableAllDays
            />
          </div>
          <div className={classes.switchContainer}>
            <Typography component="div" className={classes.switch}>
              <Grid
                component="label"
                container
                alignItems="center"
                justify="flex-start">
                <Grid item>
                  <Switch
                    name="active"
                    color="primary"
                    onChange={() => {
                      setfilters({
                        ...filters,
                        includeExpired: !filters.includeExpired
                      })
                    }}
                    checked={filters.includeExpired}
                  />
                </Grid>
              </Grid>
            </Typography>
            <Typography variant="body1">
              {getTranslate('Include Expired')}
            </Typography>
          </div>
        </div>
        <div className={classes.ButtonsContainer}>
          <Button
            className={classes.resetButton}
            variant="text"
            color="secondary"
            onClick={() => {
              settimeRangeDateObject({})
              setfilters({
                ...filters,
                status: undefined,
                timeRange: undefined,
                includeExpired: false
              })
            }}>
            {getTranslate('Reset')}
          </Button>
          <Button
            className={classes.applyButton}
            variant="contained"
            color="primary"
            disabled={loadingForProformas}
            onClick={() => {
              getProformas({
                variables: {
                  filter: filters
                }
              })
              setPage(1)
            }}>
            {getTranslate('Apply')}
          </Button>
        </div>
      </div>
    </Grow>
  )
}
